import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import { secureUpload } from '../../services/s3UploadService';

const { FiImage, FiUploadCloud, FiCheck, FiLock, FiStar, FiUser, FiCamera, FiLayout } = FiIcons;

const defaultBanners = [
  { id: 'b1', url: 'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?q=80&w=2076&auto=format&fit=crop', name: 'Autumn Leaves' },
  { id: 'b2', url: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?q=80&w=2070&auto=format&fit=crop', name: 'Ocean Sunset' },
  { id: 'b3', url: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?q=80&w=2080&auto=format&fit=crop', name: 'Urban Art' },
  { id: 'b4', url: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?q=80&w=2070&auto=format&fit=crop', name: 'Spring Forest' },
  { id: 'b5', url: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?q=80&w=2070&auto=format&fit=crop', name: 'Serene Lake' },
  { id: 'b6', url: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=2071&auto=format&fit=crop', name: 'Mountain Peak' },
];

const loadPresets = () => {
    try {
        const saved = localStorage.getItem('em_premium_presets');
        return saved ? JSON.parse(saved) : defaultBanners;
    } catch {
        return defaultBanners;
    }
};

const VisualIdentityTab = ({ memorial, coverImage, profileImage, setCoverImage, setProfileImage }) => {
  const { user } = useAuth();
  const [isPaid, setIsPaid] = useState(true); // Default to paid for demo purposes
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [premiumBanners, setPremiumBanners] = useState(loadPresets());

  useEffect(() => {
    const handleStorage = () => setPremiumBanners(loadPresets());
    window.addEventListener('storage', handleStorage);
    window.addEventListener('em_presets_updated', handleStorage);
    return () => {
        window.removeEventListener('storage', handleStorage);
        window.removeEventListener('em_presets_updated', handleStorage);
    };
  }, []);

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploadingBanner(true);
    try {
      const { s3Url } = await secureUpload({
        file,
        ownerId: user?.id || 'guest',
        category: 'memorial_banner'
      });
      setCoverImage(s3Url);
    } catch (err) {
      console.warn('S3 upload failed, using local blob for preview:', err);
      setCoverImage(URL.createObjectURL(file));
    } finally {
      setIsUploadingBanner(false);
    }
  };

  const handleProfileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploadingProfile(true);
    try {
      const { s3Url } = await secureUpload({
        file,
        ownerId: user?.id || 'guest',
        category: 'memorial_profile'
      });
      setProfileImage(s3Url);
    } catch (err) {
      console.warn('S3 upload failed, using local blob for preview:', err);
      setProfileImage(URL.createObjectURL(file));
    } finally {
      setIsUploadingProfile(false);
    }
  };

  if (!isPaid) {
    return (
      <div className="bg-brand-light p-10 rounded-3xl border border-brand-dark text-center space-y-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4">
           <button onClick={() => setIsPaid(true)} className="text-[10px] text-text-muted hover:text-accent-gold uppercase tracking-widest font-black">Simulate Upgrade</button>
        </div>
        <div className="w-20 h-20 bg-accent-gold/10 rounded-full flex items-center justify-center mx-auto text-accent-gold mb-4">
          <SafeIcon icon={FiLock} className="w-10 h-10" />
        </div>
        <h3 className="text-2xl font-serif text-accent-navy">Premium Visual Identity</h3>
        <p className="text-text-muted max-w-md mx-auto text-sm">
          Upgrade to a premium tier to unlock custom banners, high-resolution profile pictures, and exclusive premium themes.
        </p>
        <button className="btn-primary py-3 px-8 mt-4" onClick={() => setIsPaid(true)}>Upgrade to Premium</button>
      </div>
    );
  }

  return (
    <div className="space-y-10">
       <div className="flex justify-end">
           <button onClick={() => setIsPaid(false)} className="text-[10px] text-text-muted hover:text-red-500 uppercase tracking-widest font-bold">Simulate Free Tier</button>
       </div>

       {/* Profile Picture Section */}
       <div className="space-y-4">
         <div className="flex items-center gap-2 mb-4">
            <SafeIcon icon={FiUser} className="text-accent-gold w-5 h-5" />
            <h4 className="font-bold text-accent-navy">Profile Picture</h4>
         </div>
         <div className="flex flex-col md:flex-row items-center md:items-start gap-6 p-6 bg-brand-light rounded-3xl border border-brand-dark">
            <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-xl flex-shrink-0 relative group">
               <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
               <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <SafeIcon icon={FiCamera} className="text-white w-6 h-6" />
               </div>
            </div>
            <div className="flex-1 text-center md:text-left">
               <p className="text-xs text-text-muted mb-4 max-w-sm">Upload a high-quality photo. We recommend an image at least 400x400px.</p>
               <label className="btn-secondary py-2 px-6 text-xs inline-flex items-center gap-2 cursor-pointer">
                  {isUploadingProfile ? (
                     <div className="w-4 h-4 border-2 border-accent-navy/30 border-t-accent-navy rounded-full animate-spin" />
                  ) : (
                     <><SafeIcon icon={FiUploadCloud} /> Upload Photo</>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={handleProfileUpload} disabled={isUploadingProfile} />
               </label>
            </div>
         </div>
       </div>

       {/* Banner Section */}
       <div className="space-y-4">
         <div className="flex items-center gap-2 mb-4">
            <SafeIcon icon={FiLayout} className="text-accent-gold w-5 h-5" />
            <h4 className="font-bold text-accent-navy">Banner Image</h4>
         </div>

         <div className="space-y-6">
            {/* Custom Upload */}
            <div className="p-6 bg-brand-light rounded-3xl border border-brand-dark">
               <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-bold text-accent-navy">Custom Banner</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-accent-gold bg-accent-gold/10 px-2 py-1 rounded-full">Pro</span>
               </div>
               <div className="relative h-32 rounded-2xl overflow-hidden border-4 border-white shadow-sm mb-4 group">
                  <img src={coverImage} alt="Cover Preview" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <label className="bg-white/20 hover:bg-white/30 backdrop-blur-md text-white py-2 px-4 rounded-full text-xs font-bold cursor-pointer transition-all flex items-center gap-2">
                          {isUploadingBanner ? (
                             <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                             <><SafeIcon icon={FiUploadCloud} /> Replace Banner</>
                          )}
                          <input type="file" accept="image/*" className="hidden" onChange={handleBannerUpload} disabled={isUploadingBanner} />
                      </label>
                  </div>
               </div>
               <p className="text-[11px] text-text-muted">Recommended size: 1920x1080px. Max 3 custom uploads allowed per memorial.</p>
            </div>

            {/* Premium Presets */}
            <div>
               <h5 className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiStar} className="text-accent-gold" /> Premium Presets
               </h5>
               <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {premiumBanners.map(banner => (
                     <button
                        key={banner.id}
                        onClick={() => setCoverImage(banner.url)}
                        className={`relative h-20 rounded-xl overflow-hidden border-2 transition-all ${coverImage === banner.url ? 'border-accent-gold shadow-md' : 'border-transparent hover:border-brand-dark'}`}
                     >
                        <img src={banner.url} alt={banner.name} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-2">
                           <span className="text-white text-[9px] font-bold truncate">{banner.name}</span>
                        </div>
                        {coverImage === banner.url && (
                           <div className="absolute top-1 right-1 w-5 h-5 bg-accent-gold rounded-full flex items-center justify-center text-white shadow-sm">
                              <SafeIcon icon={FiCheck} className="w-3 h-3" />
                           </div>
                        )}
                     </button>
                  ))}
               </div>
            </div>
         </div>
       </div>
    </div>
  );
};

export default VisualIdentityTab;